import { useState, useMemo } from 'react';
import { Search } from 'lucide-react';

import { useProviderCities } from '@/api/usePresentationWizard';
import { StepShell } from '../components';
import styles from './SelectCity.module.css';

// ── City → country flag mapping ──────────────────────────

const PERU_CITIES = new Set([
  'Lima', 'Arequipa', 'Trujillo', 'Chiclayo', 'Cusco', 'Piura', 'Iquitos',
  'Huancayo', 'Chimbote', 'Pucallpa', 'Tacna', 'Ica', 'Juliaca', 'Ayacucho',
  'Cajamarca', 'Huanuco', 'Tarapoto', 'Puno', 'Tumbes', 'Huaraz', 'Moyobamba',
  'Chachapoyas', 'Puerto Maldonado', 'Abancay', 'Moquegua', 'Cerro de Pasco',
  'Huancavelica', 'Sullana', 'Chincha', 'Nazca',
]);

const MEXICO_CITIES = new Set([
  'Ciudad de Mexico', 'Guadalajara', 'Monterrey', 'Puebla', 'Cancun', 'Queretaro',
  'Tijuana', 'Leon MX', 'Merida', 'Chihuahua', 'San Luis Potosi', 'Aguascalientes',
  'Hermosillo', 'Saltillo', 'Mexicali', 'Culiacan', 'Zapopan', 'Ecatepec',
  'Ciudad Juarez', 'Toluca', 'Morelia', 'Cuernavaca', 'Oaxaca', 'Veracruz',
  'Villahermosa', 'Tuxtla Gutierrez', 'Acapulco', 'Playa del Carmen',
  'San Miguel de Allende', 'Los Cabos', 'Puerto Vallarta', 'Mazatlan',
  'Durango', 'Tampico', 'Pachuca',
]);

const US_CITIES = new Set([
  'Miami', 'New York', 'Los Angeles', 'Houston', 'Chicago', 'San Francisco',
  'Washington', 'Dallas', 'Orlando', 'San Diego',
]);

function getCityFlag(city: string): string {
  if (PERU_CITIES.has(city)) return '\u{1F1F5}\u{1F1EA}';
  if (MEXICO_CITIES.has(city)) return '\u{1F1F2}\u{1F1FD}';
  if (US_CITIES.has(city)) return '\u{1F1FA}\u{1F1F8}';
  return '\u{1F1EA}\u{1F1F8}'; // Spain as default (remaining cities are Spanish)
}

interface Props {
  selected: string;
  onSelect: (city: string) => void;
}

export const SelectCity = ({ selected, onSelect }: Props) => {
  const { data: cities, isLoading } = useProviderCities();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!cities) return [];
    if (!search.trim()) return cities;
    const q = search.toLowerCase().trim();
    return cities.filter((c) => c.toLowerCase().includes(q));
  }, [cities, search]);

  return (
    <StepShell
      title="Select City"
      subtitle="Choose the city for your event"
      loading={isLoading}
      loadingMessage="Loading available cities..."
    >
      <div className={styles.searchWrapper}>
        <Search size={16} className={styles.searchIcon} />
        <input
          className={styles.searchInput}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search cities..."
        />
      </div>

      <div className={styles.grid}>
        {filtered.length === 0 ? (
          <p className={styles.empty}>No cities found</p>
        ) : (
          filtered.map((city) => (
            <button
              key={city}
              type="button"
              className={`${styles.cityCard} ${selected === city ? styles.cityCardSelected : ''}`}
              onClick={() => onSelect(city)}
            >
              <span className={styles.cityFlag}>{getCityFlag(city)}</span>
              <span className={styles.cityName}>{city}</span>
            </button>
          ))
        )}
      </div>
    </StepShell>
  );
};
